import { Injectable, NgZone } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../environments/environment';

// The Google Identity Services (GIS) library is loaded via a <script> tag in index.html and
// exposes a global `google` object. It is not an npm dependency, so we declare it here.
declare const google: any;

/**
 * Thin wrapper around Google Identity Services. Renders the "Sign in with Google" button and
 * emits the resulting credential (an ID token) which the backend verifies server-side.
 *
 * The GIS callback fires OUTSIDE Angular's zone, so emissions are wrapped in NgZone.run so
 * that subscribers' navigation/change-detection work as expected.
 */
@Injectable({ providedIn: 'root' })
export class GoogleAuthService {
    private credential$ = new Subject<string>();
    private initialized = false;

    constructor(private zone: NgZone) {}

    /** Emits the Google credential (ID token) each time the user completes Google sign-in. */
    onCredential(): Observable<string> {
        return this.credential$.asObservable();
    }

    /** True only when a Google client id is configured for this environment. */
    isConfigured(): boolean {
        return !!environment.googleClientId;
    }

    private isScriptReady(): boolean {
        return typeof google !== 'undefined' && !!google?.accounts?.id;
    }

    // The GIS script is loaded async/defer, so it may not be ready when a component renders.
    private waitForScript(timeoutMs = 10000): Promise<boolean> {
        return new Promise((resolve) => {
            const start = Date.now();
            const check = () => {
                if (this.isScriptReady()) {
                    resolve(true);
                } else if (Date.now() - start > timeoutMs) {
                    resolve(false);
                } else {
                    setTimeout(check, 100);
                }
            };
            check();
        });
    }

    private init(): void {
        if (this.initialized) return;
        google.accounts.id.initialize({
            client_id: environment.googleClientId,
            callback: (response: any) => {
                if (response?.credential) {
                    this.zone.run(() =>
                        this.credential$.next(response.credential),
                    );
                }
            },
        });
        this.initialized = true;
    }

    /**
     * Renders the Google button into the given element. Resolves false if Google sign-in is
     * not configured or the script never loaded, so callers can show a fallback.
     */
    async renderButton(
        element: HTMLElement,
        options: Record<string, unknown> = {},
    ): Promise<boolean> {
        if (!this.isConfigured()) return false;
        const ready = await this.waitForScript();
        if (!ready) return false;
        this.init();
        google.accounts.id.renderButton(element, {
            theme: 'filled_black',
            size: 'large',
            type: 'standard',
            text: 'continue_with',
            shape: 'pill',
            logo_alignment: 'left',
            width: 200,
            ...options,
        });
        return true;
    }
}
